/* eslint-disable object-curly-newline */
import _defaultTo from 'lodash/defaultTo';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _toUpper from 'lodash/toUpper';
// Serialization
import { deserialize, serialize } from 'serializr';
import { customerSchema } from './schemas';
// Colors
import { customersAccountStatus } from '../../components/core/colors';
// Fragments
import { customerFragment } from './fragments';
// Permissions
import permissions from '../../constants/Permissions';
// Models
import mParkingCategory from './parkingCategory';
import mParking from './parking';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';
import { joinNonEmpty, formatPhoneNumberSubmit, findInList } from '../../utils/Tools';

// Permissions used in model
const {
  pCpm: { parkingCategoryRead, parkingRead, planningRead, bookingRead },
} = permissions;

/**
 * Serialization
 */
const deserializeCustomer = (data) => deserialize(customerSchema, data);

const serializeCustomer = (
  { parkingCategoryId, lastname, firstname, email, phone, comments, spaceNumber },
  hasPermission,
) => ({
  ...serialize(customerSchema, {
    ...(hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) && {
      parkingCategoryId: _defaultTo(parkingCategoryId, null),
    }),
    ...(comments && { comments }),
    ...(email && { email }),
    ...(firstname && { firstname }),
    lastname: _defaultTo(lastname, null),
    ...(phone && { phone: formatPhoneNumberSubmit(phone) }),
    ...(spaceNumber && { spaceNumber }),
  }),
});

/**
 * Lists
 */
const customerAccountStatusList = [
  {
    key: 'inactive',
    value: 'inactive',
    labelId: 'account.status.inactive',
    icon: 'btnEmailToSend',
    tooltipId: 'tooltip.sendInvitationBookingModule',
    color: customersAccountStatus.inactive,
    active: false,
  },
  {
    key: 'pending',
    value: 'pending',
    labelId: 'account.status.pending',
    icon: 'btnDeleteCustomer',
    tooltipId: 'tooltip.deleteAccessBookingModule',
    color: customersAccountStatus.pending,
    active: true,
  },
  {
    key: 'active',
    value: 'active',
    labelId: 'account.status.active',
    icon: 'btnDeleteCustomer',
    tooltipId: 'tooltip.deleteAccessBookingModule',
    color: customersAccountStatus.active,
    active: true,
  },
];

/**
 * Utils
 */
const customerFullName = (customerData) => (customerData ? joinNonEmpty([customerData.firstname, customerData.lastname], ' ') : '');
const customerTitle = (customerData) => customerFullName(customerData);
const customerGetParkingCategoryId = (customerData) => _get(customerData, 'parkingCategory.id');
const customerIsOnDemand = (customerData) => mParkingCategory.isOnDemand(_get(customerData, 'parkingCategory'));
const bookingCustomerFullName = (customerData) => (customerData ? joinNonEmpty([customerData.customerFirstname, customerData.customerLastname], ' ') : '');

// Status
const customerAccountStatus = (accountData) => findInList(customerAccountStatusList, 'value', accountData, 'status');

/**
 * GraphQL
 */
const queryPermissions = (hasPermission) => ({
  withParkingCategory: hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }),
  withParkings: hasPermission({ needed: parkingRead, permissionsOrContext: 'carparkPermissions' }),
  withBookings: hasPermission({ needed: bookingRead, permissionsOrContext: 'carparkPermissions' }),
  withPlanning: hasPermission({ needed: planningRead, permissionsOrContext: 'carparkPermissions' }),
});

const parkingsFilters = {
  inProgress: _toUpper(mParking.getStatusInProgress.value),
  started: _toUpper(mParking.getStatusStarted.value),
};

const queListCustomers = (hasPermission) => `
  query ListCustomers($input: ListCustomersInput!, $pagination: PaginationInput, $search: ListCustomersSearchInput, $sort: [ListCustomersSortInput]) {
    listCustomers(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...customerFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${customerFragment(queryPermissions(hasPermission), parkingsFilters)}
`;

const queGetCustomer = (hasPermission) => `
  query GetCustomer($input: GetCustomerInput!) {
    getCustomer(input: $input) {
      ...customerFragment
    }
  }

  ${customerFragment(queryPermissions(hasPermission), parkingsFilters)}
`;

const mutAddCustomer = `
  mutation AddCustomer($input: AddCustomerInput!) {
    addCustomer(input: $input) {
      id
    }
  }
`;

const mutUpdateCustomer = `
  mutation UpdateCustomer($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) {
      id
    }
  }
`;

const mutDeleteCustomer = `
  mutation DeleteCustomer($input: DeleteCustomerInput!) {
    deleteCustomer(input: $input) {
      id
    }
  }
`;

const mutCreateCustomerAccount = `
  mutation CreateCustomerAccount($input: CustomerAccountInput!) {
    createCustomerAccount(input: $input) {
      id
    }
  }
`;

const mutDeleteCustomerAccount = `
  mutation DeleteCustomerAccount($input: CustomerAccountInput!) {
    deleteCustomerAccount(input: $input) {
      id
    }
  }
`;

/**
 * Model
 */
const customer = {
  // Name and icon
  name: 'customer',
  icon: 'customer',

  // Serialization
  deserialize: deserializeCustomer,
  serialize: serializeCustomer,

  // Lists
  accountStatusList: customerAccountStatusList,

  // Utils
  title: customerTitle,
  fullName: customerFullName,
  getParkingCategoryId: customerGetParkingCategoryId,
  isOnDemand: customerIsOnDemand,
  accountStatus: customerAccountStatus,
  bookingCustomerFullName,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'lastname', dir: 'asc' }],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'lastname',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 1,
      sm: 1 + (hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      md: 3 + (hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      lg:
        4
        + (hasPermission({ needed: parkingRead, permissionsOrContext: 'carparkPermissions' }) | 0)
        + (hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) | 0)
        + (hasPermission({ needed: bookingRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      xl:
        4
        + (hasPermission({ needed: parkingRead, permissionsOrContext: 'carparkPermissions' }) | 0)
        + (hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) | 0)
        + (hasPermission({ needed: bookingRead, permissionsOrContext: 'carparkPermissions' }) | 0),
    }),
    columns: [
      {
        key: 'lastname',
        labelId: 'field.lastname',
        field: 'lastname',
        sortable: true,
      },
      {
        key: 'firstname',
        labelId: 'field.firstname',
        field: 'firstname',
        sortable: true,
        secondarySorts: [{ field: 'lastname', dir: 'asc' }],
        className: 'hidden-md-down',
      },
      {
        key: 'parkingCategory',
        permissions: {
          needed: parkingCategoryRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parkingCategory',
        field: 'parkingCategoryName',
        sortable: true,
        secondarySorts: [{ field: 'lastname', dir: 'asc' }],
        className: 'hidden-xs-down',
      },
      {
        key: 'account',
        labelId: 'field.account',
        field: 'account',
        sortable: false,
        className: 'table-content-2 hidden-sm-down',
      },
      {
        key: 'spaceNumber',
        labelId: 'field.spaceNumber',
        field: 'spaceNumber',
        sortable: true,
        secondarySorts: [
          { field: 'lastname', dir: 'asc' },
          { field: 'firstname', dir: 'asc' },
        ],
        className: 'table-content-2 hidden-sm-down',
      },
      {
        key: 'bookings',
        permissions: {
          needed: bookingRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.bookings',
        field: 'bookings',
        sortable: false,
        className: 'table-content-2 table-content-center hidden-md-down',
      },
      {
        key: 'parkings',
        permissions: {
          needed: parkingRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.accessRights',
        field: 'parkings',
        sortable: false,
        className: 'table-content-2 table-content-center hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        hidden: true,
        key: 'onDemand',
        labelId: '',
        field: 'onDemand',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'onDemand',
        },
      },
      {
        key: 'lastname',
        labelId: 'field.lastname',
        field: 'lastname',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'lastname',
        },
      },
      {
        key: 'firstname',
        labelId: 'field.firstname',
        field: 'firstname',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'firstname',
        },
      },
      {
        key: 'parkingCategoryId',
        permissions: {
          needed: parkingCategoryRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parkingCategory',
        field: 'parkingCategoryId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'parkingCategoryId',
        },
      },
      {
        key: 'email',
        labelId: 'field.email',
        field: 'email',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'email',
        },
      },
      {
        key: 'phone',
        labelId: 'field.phoneNumber',
        field: 'phone',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'phone',
        },
      },
      {
        key: 'spaceNumber',
        labelId: 'field.spaceNumber',
        field: 'spaceNumber',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        scope: 'eq',
        filter: {
          field: 'spaceNumber',
        },
      },
      {
        key: 'withStartedParkings',
        permissions: {
          needed: parkingRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.withStartedParkings',
        field: 'withStartedParkings',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'startedParkings',
        },
      },
      {
        key: 'withStartedBookings',
        permissions: {
          needed: bookingRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.withStartedBookings',
        field: 'withStartedBookings',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'startedBookings',
        },
      },
    ],
  },

  // List features for parkings add access
  chooseListFeatures: (withParkings, withBookings) => ({
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'lastname', dir: 'asc' }],
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 1,
      sm: 1,
      md: 3,
      lg:
        3
        + ((hasPermission({ needed: parkingRead, permissionsOrContext: 'carparkPermissions' }) && withParkings) | 0)
        + ((hasPermission({ needed: bookingRead, permissionsOrContext: 'carparkPermissions' }) && withBookings) | 0),
      xl:
        3
        + ((hasPermission({ needed: parkingRead, permissionsOrContext: 'carparkPermissions' }) && withParkings) | 0)
        + ((hasPermission({ needed: bookingRead, permissionsOrContext: 'carparkPermissions' }) && withBookings) | 0),
    }),
    columns: [
      {
        key: 'customer',
        labelId: 'field.carparkUser',
        field: 'customer',
        sortable: false,
      },
      {
        key: 'customerEmail',
        labelId: 'field.carparkUserEmail',
        field: 'customerEmail',
        sortable: false,
      },
      {
        key: 'account',
        labelId: 'field.account',
        field: 'account',
        sortable: false,
        className: 'table-content-2 hidden-sm-down',
      },
      {
        key: 'spaceNumber',
        labelId: 'field.spaceNumber',
        field: 'spaceNumber',
        sortable: true,
        className: 'table-content-2 hidden-sm-down',
      },
      {
        key: 'parkings',
        hidden: !withParkings,
        permissions: {
          needed: parkingRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.accessRights',
        field: 'parkings',
        sortable: false,
        className: 'table-content-2 table-content-center hidden-md-down',
      },
      {
        key: 'bookings',
        hidden: !withBookings,
        permissions: {
          needed: bookingRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.bookings',
        field: 'bookings',
        sortable: false,
        className: 'table-content-2 table-content-center hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [],
  }),

  // Parkings list features
  parkingsListFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'begin', dir: 'asc' }],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 2,
      lg: 2,
      xl: 2,
    }),
    columns: [
      {
        key: 'begin',
        labelId: 'field.beginDate',
        field: 'begin',
        sortable: false,
      },
      {
        key: 'end',
        labelId: 'field.endDate',
        field: 'end',
        sortable: false,
      },
    ],

    searchFields: [],
  },

  // Accesses list features
  accessesListFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'date', dir: 'desc' }],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 2,
      lg: 4,
      xl: 4,
    }),
    columns: [
      {
        key: 'date',
        labelId: 'field.date',
        field: 'date',
        sortable: false,
      },
      {
        key: 'door',
        labelId: 'field.access',
        field: 'door',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'source',
        labelId: 'field.accessWay',
        field: 'source',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'result',
        labelId: 'field.result',
        field: 'result',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'resultSmDown',
        labelId: 'field.result',
        field: 'result',
        sortable: false,
        className: 'hidden-md-up table-content-1',
      },
    ],

    searchFields: [],
  },

  // GraphQL
  queListCustomers,
  queGetCustomer,
  mutAddCustomer,
  mutUpdateCustomer,
  mutDeleteCustomer,
  mutCreateCustomerAccount,
  mutDeleteCustomerAccount,
};

module.exports = customer;



// WEBPACK FOOTER //
// ./models/cpm/customer.js